import React, { useState } from 'react';
import { ScrollView, ImageBackground } from 'react-native';
import {
  Text,
  Heading,
  InfoIcon,
  ChevronRightIcon,
  Box,
  VStack,
  HStack,
  Pressable,
  Modal,
  Divider,
} from 'native-base';
import { foodList } from '../../constants/foodList';
import { CircledCheckSVG } from '../CustomSVGs';
import FoodGuideInfo from './FoodGuideInfo';
import { useDashboardPageState } from '../../hooks/useDashboardPageState';

export const placeholderFood = [
  'Wine',
  'Cruciferous Vegetables',
  'Seeds',
  'Beans, Peas, & Lentils',
  'Wheat Products',
  'Cereal & Granola (Including Bars)',
  'Soft Cheese',
  'Milk & Cream',
  'Yogurt',
  'Berries',
  'Dried Fruit',
  'Treenuts',
  'Sweeteners',
  'Herbal Tea',
  'Caffeinated Tea & Coffee',
  'Soda & Energy Drinks',
  'Spicy Food',
  'Fried & Fast Food',
  'Beer, Cider, & Hard Seltzer',
  'Juice & Sports Drinks',
  'Packaged Condiments',
];

export const FoodGuide = () => {
  const {
    state: { current_subphase, user },
  } = useDashboardPageState();
  const [modalOpen, setModalOpen] = useState(false);
  const [isInfoVis, setIsInfoVis] = useState(true);
  const [isFoodGuideModalVis, setIsFoodGuideModalVis] = useState(false);
  const foodGuideImage = './../../food-guide.png';
  const doNotSymbol = './../../do-not-symbol.png';
  const closeBttn = './../../close-icon.png';

  const styles = {
    foodGuide: {
      width: 88,
      height: 88,
    },
    symbol: {
      width: 20,
      height: 20,
    },
    backBttn: {
      width: 32,
      height: 32,
    },
    icon: {
      width: 32,
      height: 32,
    },
    closeContainer: {
      position: 'absolute',
      top: 0,
      right: 15,
      height: 32,
      width: 32,
    },
    foodIcon: {
      width: 52,
      height: 52,
    },
  };

  const currentFoodList = () => {
    const phase = user.current_phase;
    if (phase === 'PhaseTwo') {
      return user.potential_triggers.length > 0
        ? user.potential_triggers
        : user.recommended_foods;
    } else if (phase === 'PhaseThree') {
      const inProgress = current_subphase;
      return inProgress?.food_category
        ? [inProgress.food_category]
        : user.suspected_triggers;
    } else if (phase === 'PhaseFour') {
      // TODO: @Brenden, @Nick -> fallback here is using a list that should exist since placeholder food might not include foods selected
      return user.verified_triggers?.length > 0
        ? user.verified_triggers
        : user.suspected_triggers;
    } else {
      return placeholderFood;
    }
  };

  const phaseNumber = () => {
    switch (user.current_phase) {
      case 'PhaseOne':
        return '1';
      case 'PhaseTwo':
        return '2';
      case 'PhaseThree':
        return '3';
      case 'PhaseFour':
        return '4';
      default:
        return '1';
    }
  };

  const FoodGuideModal = () => {
    return (
      <>
        <Modal size={'lg'} isOpen={modalOpen} flex={1} px={0} top={0}>
          <Modal.Content>
            <Modal.Header>
              <HStack justifyContent={'center'}>
                <Pressable
                  overflow={'hidden'}
                  onPress={() => setIsFoodGuideModalVis(true)}
                >
                  <Heading textAlign={'center'}>Food Guide</Heading>
                </Pressable>
                <Box style={styles.closeContainer}>
                  <Pressable onPress={() => setModalOpen(false)}>
                    <ImageBackground
                      resizeMode="contain"
                      source={closeBttn}
                      style={styles.icon}
                    />
                  </Pressable>
                </Box>
              </HStack>
            </Modal.Header>

            <Modal.Body>
              <ScrollView>
                {isInfoVis && (
                  <Box mb={5} p={4} bg={'info.100'} borderRadius={8}>
                    <HStack justifyContent={'space-between'}>
                      <InfoIcon color={'info.600'} mr={4} />
                      <Box flexShrink={'1'}>
                        <Text fontWeight={500} fontSize={'md'}>
                          Your Food Guide
                        </Text>
                        <Text fontWeight={400} mt={1}>
                          Use this guide to help you choose delicious
                          alternatives for your eliminated foods
                        </Text>
                      </Box>
                      <Pressable onPress={() => setIsInfoVis(false)}>
                        <ImageBackground
                          resizeMode="contain"
                          source={closeBttn}
                          style={{ height: 20, width: 20 }}
                          mr={4}
                        />
                      </Pressable>
                    </HStack>
                  </Box>
                )}

                {currentFoodList().map((item, index) => (
                  <VStack key={index}>
                    <VStack>
                      <HStack alignItems={'center'} mb={3}>
                        <ImageBackground
                          source={
                            item.food_category
                              ? foodList[item.food_category]?.imgURL
                              : foodList[item]?.imgURL
                          }
                          resizeMode="cover"
                          style={styles.foodIcon}
                        />
                        <Heading
                          fontSize={'2xl'}
                          fontFamily="heading"
                          color={'secondary.600'}
                          ml={3}
                        >
                          {item.food_category
                            ? foodList[item.food_category].name
                            : foodList[item].name}
                        </Heading>
                      </HStack>

                      <div>
                        <HStack mt={4}>
                          <ImageBackground
                            source={doNotSymbol}
                            resizeMode="cover"
                            style={styles.symbol}
                          />
                          <VStack ml={2} w={'100%'}>
                            <Text mb={2} fontWeight={500} fontSize={'lg'}>
                              Avoid These
                            </Text>
                            <Text fontSize={'md'} pr={7}>
                              {item.food_category
                                ? foodList[item.food_category].avoidThese
                                : foodList[item].avoidThese}
                            </Text>
                          </VStack>
                        </HStack>
                      </div>

                      <div>
                        <HStack mt={6}>
                          <CircledCheckSVG fill="#2D6670" size={'20px'} />
                          <VStack ml={2} w={'100%'}>
                            <Text mb={2} fontWeight={500} fontSize={'lg'}>
                              Instead, Try These
                            </Text>
                            <Text fontSize={'md'} pr={7}>
                              {item.food_category
                                ? foodList[item.food_category].eatThisInstead
                                : foodList[item].eatThisInstead}
                            </Text>
                          </VStack>
                        </HStack>
                      </div>
                    </VStack>
                    {index !== currentFoodList().length - 1 && (
                      <Divider my={5} />
                    )}
                  </VStack>
                ))}
              </ScrollView>
            </Modal.Body>
          </Modal.Content>
        </Modal>
        <FoodGuideInfo
          isFoodGuideModalVis={isFoodGuideModalVis}
          setIsFoodGuideModalVis={setIsFoodGuideModalVis}
        />
      </>
    );
  };

  return (
    <div>
      <Pressable onPress={() => setModalOpen(true)}>
        <HStack alignItems={'center'} justifyContent={'space-between'}>
          <VStack alignItems={'flex-start'} maxWidth={'75%'}>
            <Heading display={'flex'}>
              Your Food Guide <ChevronRightIcon ml={3} size={'lg'} />{' '}
            </Heading>
            {phaseNumber() !== '3' && (
              <Text>
                A delicious guide to alternate foods to replace the foods you're
                avoiding.
              </Text>
            )}
            {phaseNumber() === '3' && (
              <Text>
                A delicious guide to alternate foods to replace your excluded
                foods.
              </Text>
            )}
          </VStack>
          <VStack>
            <ImageBackground source={foodGuideImage} style={styles.foodGuide} />
          </VStack>
        </HStack>
      </Pressable>
      <FoodGuideModal />
    </div>
  );
};
